// product-carousel.js

// Wait for jQuery and Slick to be loaded
if (typeof jQuery === 'undefined') {
  console.error('jQuery must be loaded before product-carousel.js');
}

const initProductCarousels = () => {
  const carouselOptions = {
    slidesToShow: 1,
    slidesToScroll: 1,
    // infinite:false removes Slick's cloned first/last slides. Clones copy
    // `<img loading="lazy">` without ever firing their load, which left blank/white
    // slides on scroll — the bug forceCarouselImagesLoad was papering over.
    infinite: false,
    dots: false,
    draggable: true,
    arrows: true,
    // PM Fix: Hide the left arrow, keep the right arrow
    prevArrow: '<button type="button" class="slick-prev" style="display: none !important;"></button>',
    nextArrow: '<button type="button" class="slick-next"></button>',
    useCSS: true,
    useTransform: true
  };

  // Debounce function to limit execution frequency
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Store initialized carousels to avoid redundant checks
  const initializedCarousels = new WeakSet();
  
  // Store the observer for reuse
  let carouselObserver;

  // Force a single image to fetch + decode now. Blink/Gecko honor flipping
  // `loading` to eager post-parse; WebKit/Safari ignores it, and inside Slick's
  // transform track the native lazy heuristic treats the off-screen (translateX'd,
  // overflow:hidden) slide as never visible and defers the fetch indefinitely.
  // decode() is the reliable cross-engine forcing primitive. A per-image guard flag
  // keeps repeat calls (observer + swipe + queue can all target the same img) cheap.
  const warmImage = (img, priority) => {
    if (!img || img.dataset.carouselWarmed === 'true') return;
    img.dataset.carouselWarmed = 'true';
    img.loading = 'eager';
    if (priority) {
      img.setAttribute('fetchpriority', priority);
    }
    if (typeof img.decode === 'function' && !img.complete) {
      img.decode().catch(() => {});
    }
  };

  // Promote a slide's lazy images (used for the first slides at init and for swipe
  // neighbors just-in-time in beforeChange).
  const eagerLoadSlide = ($slide) => {
    $slide.find('img[loading="lazy"]').each(function() {
      warmImage(this, 'auto');
    });
  };

  // Start buffering a slide's video ahead of it being centered. The clip ships
  // preload="none" (so it never competes with images until needed); flipping it to
  // preload="auto" and calling load() when the slide is the swipe target means the
  // first frame is decoded by the time observeCardVideos() plays it — shrinking the
  // poster→frame gap that iOS Safari otherwise shows as a white flash on swipe-in.
  const warmSlideVideo = ($slide) => {
    const video = $slide.find('video[data-card-video]')[0];
    if (!video || video.dataset.videoWarmed === 'true') return;
    video.dataset.videoWarmed = 'true';
    video.preload = 'auto';
    try { video.load(); } catch (e) {}
  };

  const forceCarouselImagesLoad = ($carousel) => {
    // With infinite:false there are no clones, so native lazy-load works inside the
    // track. We only need the first two slides ready before init; the rest load
    // on demand as the user swipes (see beforeChange handler below).
    $carousel.find('.carousel-slide').slice(0, 2).each(function() {
      eagerLoadSlide($(this));
    });

    // The first visible slide is what the shopper sees as the card scrolls in.
    // Force its image to fetch immediately (independent of Slick clone timing) so it
    // is never blank/white on fast mobile scroll.
    // NOTE: never re-assign `src`/`srcset` to "restart" a load — mutating a
    // responsive image makes WebKit abort the in-flight request and render the
    // broken-image icon. warmImage only flips loading/fetchpriority + decode().
    const firstImg = $carousel.find('.carousel-slide:first-child img').first()[0]
      || $carousel.find('img').first()[0];
    warmImage(firstImg, 'high');
  };

  // Once a shopper engages ONE carousel, warm all of its slides so swiping never
  // hiccups. A card holds only ~6 slides, so this is cheap and stays scoped to the
  // single card in use, not the whole grid. Uses a time-sliced setTimeout queue
  // (NOT requestIdleCallback): Safari delays idle callbacks during active
  // scroll/touch gestures, so idle warming can run only after the user has already
  // swiped into a blank slide. The setTimeout pacing also keeps Safari from
  // decoding every remaining slide in one frame.
  const warmCarouselImages = ($carousel) => {
    if ($carousel.data('warmed')) return;
    $carousel.data('warmed', true);

    const imgs = $carousel.find('img[loading="lazy"]').toArray();
    let index = 0;
    const pump = () => {
      const startedAt = performance.now();
      while (index < imgs.length && performance.now() - startedAt < 8) {
        warmImage(imgs[index], 'auto');
        index += 1;
      }
      if (index < imgs.length) {
        setTimeout(pump, 60);
      }
    };
    pump();
  };

  // Function to initialize a single carousel
  const initializeCarousel = ($carousel) => {
    const element = $carousel[0];
    
    // Skip if already initialized or currently initializing
    if (initializedCarousels.has(element)) {
      if ($carousel.hasClass('slick-initialized')) {
        $carousel.slick('setPosition');
      }
      return;
    }
    
    initializedCarousels.add(element);
    forceCarouselImagesLoad($carousel);
    
    // Recalc layout once slide images finish loading (fixes mobile collapse when
    // Slick initializes before images have dimensions)
    $carousel.on('init', function () {
      $carousel.find('img').each(function () {
        if (!this.complete) {
          this.addEventListener('load', () => {
            if ($carousel.hasClass('slick-initialized')) {
              $carousel.slick('setPosition');
            }
          }, { once: true });
        }
      });
    });

    // Load slides just-in-time as the user swipes: the incoming slide and its
    // immediate neighbors. Keeps the initial payload small while never showing
    // a blank slide on navigation.
    $carousel.on('beforeChange', function (event, slick, current, next) {
      const $slides = slick.$slides;
      [next, next + 1, next - 1].forEach((i) => {
        if (i >= 0 && i < $slides.length) {
          eagerLoadSlide($($slides[i]));
        }
      });
      // Buffer the incoming slide's video now so it can paint a frame the instant it
      // lands (observeCardVideos plays it on intersect) rather than starting cold.
      if (next >= 0 && next < $slides.length) {
        warmSlideVideo($($slides[next]));
      }
    });

    // On first engagement with this carousel, warm the rest of its slides in the
    // background (see warmCarouselImages) so subsequent swipes never pop in.
    // touchstart/pointerdown fire on the shopper's intent — earlier than
    // beforeChange — so the pipeline is filling before the first transition ends.
    //
    // IMPORTANT ordering: warm the videos flanking the current slide BEFORE
    // warmCarouselImages floods the pool with the card's thumbnails. A video is far
    // larger than a thumbnail, and iOS Safari caps at ~6 connections/host; if the
    // clip's fetch queues behind that image burst it stalls until a connection frees
    // up, which is the intermittent "video loads slowly after a swipe" symptom.
    // Kicking video.load() off first lets the incoming clip grab a connection while
    // the (default-priority) image warming fills the remainder. We warm both
    // neighbors since the swipe direction isn't known until beforeChange (which still
    // warms the true target as a fallback for fast multi-slide/arrow navigation).
    $carousel.on('touchstart pointerdown swipe beforeChange', function () {
      if ($carousel.hasClass('slick-initialized')) {
        const slick = $carousel.slick('getSlick');
        const current = slick.currentSlide;
        [current - 1, current + 1].forEach((i) => {
          if (i >= 0 && i < slick.$slides.length) {
            warmSlideVideo($(slick.$slides[i]));
          }
        });
      }
      warmCarouselImages($carousel);
    });

    try {
      $carousel.slick(carouselOptions);
    } catch (error) {
      console.error('Error initializing carousel:', error);
      initializedCarousels.delete(element); // Remove on error so it can retry
    }
  };

  // Batch process carousels to prevent main thread blocking
  const batchInitialize = ($carousels) => {
    if ($carousels.length === 0) return;
    
    requestAnimationFrame(() => {
      let processed = 0;
      const batchSize = 3;
      
      const processBatch = () => {
        const batch = $carousels.slice(processed, processed + batchSize);
        batch.each(function() {
          initializeCarousel($(this));
        });
        
        processed += batchSize;
        if (processed < $carousels.length) {
          requestAnimationFrame(processBatch);
        }
      };
      
      processBatch();
    });
  };

  // Main initialization function - handles all carousels
  const initializeAllCarousels = () => {
    const $carousels = $('.swipable-card');
    batchInitialize($carousels);
  };

  // Dedicated observer for ONLY the first image of each card, decoupled from Slick.
  // Below-fold first slides are lazy (position > 4 in Liquid, and every card in a
  // featured-collection tab that isn't passed `position`). If their fetch waits for
  // the heavier initializeCarousel/Slick path, fast mobile-Safari scroll outruns it
  // and the card paints blank. This observer fires far ahead of the viewport and
  // starts fetch+decode immediately — no Slick, no rAF batching in the path — while
  // still pacing to viewport proximity (never the parse-time herd on big pages).
  let firstImageObserver;
  const observeFirstCarouselImages = (root = document) => {
    const selector = '.swipable-card .carousel-slide:first-child img[loading="lazy"]:not([data-carousel-observed="true"])';

    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll(selector).forEach((img) => {
        img.dataset.carouselObserved = 'true';
        warmImage(img, 'high');
      });
      return;
    }

    if (!firstImageObserver) {
      firstImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          warmImage(entry.target, 'high');
          firstImageObserver.unobserve(entry.target);
        });
      }, {
        // Modest lead so we warm the first slide of cards approaching the viewport
        // WITHOUT flipping dozens of images to fetchpriority=high at once. On a big
        // grid a large lead (was 1800px) fired a burst that saturated iOS Safari's
        // ~6 connections/host and starved/aborted responsive fetches. The shimmer
        // (custom-styles.liquid) now sits BEHIND the image, so a first slide that
        // isn't pre-warmed just loads natively while the shimmer shows — no blank card.
        rootMargin: '700px 0px'
      });
    }

    root.querySelectorAll(selector).forEach((img) => {
      img.dataset.carouselObserved = 'true';
      firstImageObserver.observe(img);
    });
  };
  observeFirstCarouselImages();

  // Play card videos ONLY while their slide is actually on-screen. The markup ships
  // videos with preload="none" and no autoplay (see product-card.liquid), so nothing
  // is fetched until this fires. Off-track carousel slides are translated outside the
  // overflow:hidden track, so the IO reports them as not intersecting — meaning only
  // the centered/active slide's video loads and plays; the rest stay at their poster.
  // This stops ~16 autoplaying clips from starving product images out of Safari's
  // connection pool (the white-cards-after-fast-scroll bug).
  let cardVideoObserver;

  // Reveal a video (fade it in over the poster) only once it has actually presented
  // a frame, so the blank frame iOS Safari shows while spinning up the video layer
  // stays hidden behind opacity:0. requestVideoFrameCallback fires on a real painted
  // frame; loadeddata is the fallback for engines without rVFC.
  const revealVideoOnFrame = (video) => {
    if (video.dataset.revealed === 'true') return;
    const show = () => {
      video.dataset.revealed = 'true';
      video.classList.add('is-playing');
    };
    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => show());
    } else if (video.readyState >= 2) {
      show();
    } else {
      video.addEventListener('loadeddata', show, { once: true });
    }
  };

  const playCardVideo = (video) => {
    const played = video.play();
    if (played && typeof played.then === 'function') {
      played.then(() => revealVideoOnFrame(video)).catch(() => {});
    } else {
      revealVideoOnFrame(video);
    }
  };

  const observeCardVideos = (root = document) => {
    const selector = 'video[data-card-video]:not([data-video-observed="true"])';
    const videos = root.querySelectorAll(selector);
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => {
        video.dataset.videoObserved = 'true';
        playCardVideo(video);
      });
      return;
    }

    if (!cardVideoObserver) {
      cardVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            playCardVideo(video);
          } else {
            video.pause();
          }
        });
      }, {
        // Require a real slice of the video on screen before loading/playing it, so
        // horizontally off-track slides never trigger a fetch.
        threshold: 0.5
      });
    }

    videos.forEach((video) => {
      video.dataset.videoObserved = 'true';
      cardVideoObserver.observe(video);
    });
  };
  observeCardVideos();

  // IntersectionObserver for lazy initialization of off-screen carousels
  if ('IntersectionObserver' in window) {
    carouselObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const $carousel = $(entry.target);
          if (!$carousel.hasClass('slick-initialized')) {
            initializeCarousel($carousel);
          }
          carouselObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '600px 0px' // First-slide fetch is handled by the dedicated first-image observer (700px lead); Slick init only needs a modest lead for layout
    });

    // Observe all carousel elements
    $('.swipable-card').each(function() {
      carouselObserver.observe(this);
    });
  } else {
    // Fallback for browsers without IntersectionObserver
    initializeAllCarousels();
  }

  // Handle color swatch clicks
  $(document).on('click', '.color-swatch', function() {
    const color = $(this).attr('data-tooltip');
    const $productCard = $(this).closest('.product-card');
    const productHandle = $productCard.attr('class').match(/product-card-([^\s]+)/)?.[1];
    
    if (!productHandle) return;
    
    const carousel = $(`.product-card-${productHandle} .swipable-card:visible`);
    if (!carousel.length) return;

    try {
      const slides = carousel.slick('getSlick').$slides;
      if (slides?.length) {
        for(let i = 0; i < slides.length; i++) {
          if($(slides[i]).attr('data-variant-color') === color) {
            carousel.slick('slickGoTo', i);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error handling color swatch click:', error);
    }
  });

  // Create debounced reinitialize function for dynamic content
  const debouncedReinitialize = debounce(() => {
    // Force layout recalculation
    document.body.offsetHeight;

    // Cover first images of any newly injected cards (AJAX pagination, tab
    // changes, section loads) before their carousels initialize.
    observeFirstCarouselImages();

    // Same for card videos: gate playback of any newly injected clips to visibility.
    observeCardVideos();

    // Find carousels that need refresh or initialization
    $('.swipable-card:visible').each(function() {
      const $carousel = $(this);
      if ($carousel.hasClass('slick-initialized')) {
        $carousel.slick('setPosition');
      } else if ('IntersectionObserver' in window) {
        // Re-observe new carousels that appear
        carouselObserver.observe(this);
      } else {
        initializeCarousel($carousel);
      }
    });
  }, 150);

  // Single handler for layout/visibility changes
  const handleDynamicContent = () => debouncedReinitialize();

  // Consolidate all dynamic content events
  $(document)
    .on('shopify:section:load shopify:section:select', handleDynamicContent)
    .on('page:load page:change turbolinks:load pjax:end', handleDynamicContent);

  // AJAX FIX 1: Hook directly into the theme's native progress bar loading event
  document.documentElement.addEventListener('theme:loading:end', () => {
    setTimeout(handleDynamicContent, 100); 
    setTimeout(handleDynamicContent, 500); // Safety fallback for slower DOM paints
  });

  // Handle layout switch button clicks
  $(document).on('click', 'collection-layout-switch button', handleDynamicContent);

  // Handle carousel navigation (featured collections tabs)
  $(document).on('click', 'carousel-navigation button', function() {
    const buttonId = $(this).attr('id');
    setTimeout(() => {
      $(`.featured-collections-carousel__item[data-tab-id="${buttonId}"]`)
        .find('.color-swatch')
        .first()
        .trigger('click');
    }, 200);
  });

  // AJAX FIX 2: Broaden the MutationObserver to look for any new elements added to the entire body
  const layoutObserver = new MutationObserver(
    debounce((mutations) => {
      let needsUpdate = false;
      
      mutations.forEach(mutation => {
        // Look for layout grid class changes
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          needsUpdate = true;
        }
        // Look for actual HTML elements being injected via AJAX pagination
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        handleDynamicContent();
      }
    }, 150)
  );

  // Start observing the entire body to ensure we don't miss the AJAX injection
  layoutObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true, // Crucial for catching Page 2 HTML injection
    subtree: true
  });
};

// Initialize when document is ready
$(document).ready(() => {
  try {
    if (typeof $.fn.slick === 'undefined') {
      console.error('Slick carousel must be loaded before product-carousel.js');
      return;
    }
    
    // Yield control to let other document ready handlers run first
    requestAnimationFrame(() => {
      initProductCarousels();
    });
  } catch (error) {
    console.error('Error initializing product carousels:', error);
  }
});
